/**
 * FolderBreadcrumb Component
 * 
 * Displays current folder path with navigation
 */

import React from 'react';
import { DocumentFolder } from '../types/documentsTypes';
import { ChevronRight, Home, Folder } from 'lucide-react';
import { cn } from '@/core/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface FolderBreadcrumbProps {
  folders: DocumentFolder[];
  currentFolderId: string | null;
  onNavigate: (folderId: string | null) => void;
  className?: string;
}

interface BreadcrumbItem {
  id: string | null;
  name: string;
  icon?: React.ReactNode;
}

// ============================================================================
// Helpers
// ============================================================================

const buildBreadcrumbPath = (
  folders: DocumentFolder[],
  currentFolderId: string | null
): BreadcrumbItem[] => {
  const path: BreadcrumbItem[] = [
    { id: null, name: 'All Documents', icon: <Home className="w-4 h-4" /> }
  ];

  if (!currentFolderId) return path;

  const findPath = (folderId: string): DocumentFolder[] => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return [];
    
    if (folder.parent_id) {
      return [...findPath(folder.parent_id), folder];
    }
    return [folder];
  };

  const folderPath = findPath(currentFolderId);
  folderPath.forEach(folder => {
    path.push({
      id: folder.id,
      name: folder.name,
      icon: <Folder className="w-4 h-4" />
    });
  });

  return path;
};

// ============================================================================
// Component
// ============================================================================

export const FolderBreadcrumb: React.FC<FolderBreadcrumbProps> = ({
  folders,
  currentFolderId,
  onNavigate,
  className,
}) => {
  const breadcrumbPath = buildBreadcrumbPath(folders, currentFolderId);

  return (
    <nav className={cn('flex items-center gap-1 text-sm', className)}>
      {breadcrumbPath.map((item, index) => {
        const isLast = index === breadcrumbPath.length - 1;

        return (
          <React.Fragment key={item.id ?? 'root'}>
            {index > 0 && (
              <ChevronRight className="w-4 h-4 text-warm-gray flex-shrink-0" />
            )}
            
            <button
              onClick={() => !isLast && onNavigate(item.id)}
              disabled={isLast}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded transition-colors',
                isLast
                  ? 'text-navy font-medium cursor-default'
                  : 'text-warm-gray hover:text-navy hover:bg-cream'
              )}
            >
              {item.icon}
              <span className="truncate max-w-[150px]">{item.name}</span>
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
};

// ============================================================================
// Skeleton
// ============================================================================

export const FolderBreadcrumbSkeleton: React.FC = () => (
  <div className="flex items-center gap-1">
    <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
    <ChevronRight className="w-4 h-4 text-gray-200" />
    <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
  </div>
);

export default FolderBreadcrumb;

